import { useMutation, useQuery } from "@tanstack/react-query";
import axios, { AxiosInstance, AxiosResponse } from "axios";
import { httpClient, ROOT_URL } from "../App";
import {
  ForgotPasswordProps,
  LoginApiProps,
  RegisterApiProps,
  ResetPasswordProps,
} from "./interfaces";

enum ApiPathEnum {
  Login,
  Register,
  ResetPassword,
  ForgotPassword,
  ResendVerifyEmail,
}

export class SmartApi {
  rootUrl: string;
  urlMapper: Record<ApiPathEnum, string>;
  client: AxiosInstance;

  constructor(rootUrl: string) {
    this.rootUrl = rootUrl;
    this.urlMapper = {
      [ApiPathEnum.Login]: "/api/v1/auth/user/login",
      [ApiPathEnum.Register]: "/api/v1/auth/register",
      [ApiPathEnum.ResetPassword]: "/api/v1/auth/reset-password",
      [ApiPathEnum.ForgotPassword]: "/api/v1/auth/forgot-password",
      [ApiPathEnum.ResendVerifyEmail]: "/api/v1/auth/resend-verify-email",
    };
    this.client = axios.create();
  }

  private buildUrl(path: ApiPathEnum): string {
    return `${this.rootUrl}${this.urlMapper[path]}`;
  }

  private async makeRequest(request: Promise<AxiosResponse>) {
    try {
      return await request;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async register(props: RegisterApiProps) {
    return await this.makeRequest(
      this.client.post(this.buildUrl(ApiPathEnum.Register), props)
    );
  }

  async login(props: LoginApiProps) {
    return await this.makeRequest(
      this.client.post(this.buildUrl(ApiPathEnum.Login), props)
    );
  }

  async resetPassword(props: ResetPasswordProps) {
    return await this.makeRequest(
      this.client.post(this.buildUrl(ApiPathEnum.ResetPassword), props)
    );
  }

  async forgotPassword(props: ForgotPasswordProps) {
    return await this.makeRequest(
      this.client.post(this.buildUrl(ApiPathEnum.ForgotPassword), props)
    );
  }

  async resendVerifyEmail(email: string) {
    return await this.makeRequest(
      this.client.post(this.buildUrl(ApiPathEnum.ResendVerifyEmail), { email })
    );
  }
}

export const useGetEmailByCode = (value: string) => {
  return useQuery(
    [`/v1/worldCheck/countries`],
    async () => getEmailByCode(value),
    {
      enabled: !!value,
    }
  );
};

export function getEmailByCode(value: string) {
  return httpClient.get(
    `${ROOT_URL}/api/v1/auth/reset-token/get-email?value=${value}`
  );
}

export const useChangePassword = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: changePassword,
  });
};

export function changePassword(body: {
  newPassword: string;
  oldPassword: string;
}) {
  return httpClient.post(`${ROOT_URL}/api/v1/auth/change-password`, body);
}

export const useLogin = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: login,
  });
};

export function login(body: { email: string; password: string }) {
  return httpClient.post(`${ROOT_URL}/api/v1/auth/user/login`, body);
}

export const useRegister = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: register,
  });
};

export function register(body: {
  email: string;
  password: string;
  fullName: string;
  address: string;
  website: string;
  regNo: string;
  country: string;
}) {
  return httpClient.post(`${ROOT_URL}/api/v1/auth/register`, body);
}

export const useForgotPassword = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: forgotPassword,
  });
};

export function forgotPassword(body: { email: string }) {
  return httpClient.post(`${ROOT_URL}/api/v1/auth/forgot-password`, body);
}

export const useResetPassword = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: resetPassword,
  });
};

export function resetPassword(body: { password: string; code: string | null }) {
  return httpClient.post(`${ROOT_URL}/api/v1/auth/reset-password`, body);
}

export const useCheckVerifyEmail = (email?: string) => {
  return useQuery<{ data: { message: string } }>(
    [`/api/v1/asset-config-groups/total-assets/get-all`, email],
    async () => checkVerifyEmail(email),
    {
      enabled: !!email,
    }
  );
};

export function checkVerifyEmail(email?: string) {
  return httpClient.get(
    `${ROOT_URL}/api/v1/auth/check-email-status?email=${email}`
  );
}

export const useLogout = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: logout,
  });
};

export function logout() {
  return httpClient.post(`${ROOT_URL}/api/v1/auth/logout`);
}
